/**
 * Money rounding helpers for the payroll wizard.
 *
 * JavaScript's floating-point arithmetic produces ugly results like
 * `278.30000000000007` whenever values are summed/divided. We round
 * every monetary field to 2 decimal places before it hits the DOM so
 * the wizard shows clean amounts. Use `round2()` for single values and
 * `round2PreviewRows()` to normalize an entire array of payroll-preview
 * rows in one shot.
 */

// All numeric fields we ever surface in the wizard preview, calculation
// dialog, salary slip, summary cards, or PDF export. Keep this list
// exhaustive — anything missed will still leak floating-point noise.
const MONEY_FIELDS = [
  // Earnings
  'gross_salary', 'gross_earnings', 'net_salary', 'net_earnings',
  'past_dues', 'total_deductions',
  'basic', 'housing', 'transport', 'telephone',
  'commission', 'tips', 'cod_collected', 'tlb_amount', 'bonus',
  'average_per_order',
  'overtime_pay',
  // Deductions
  'fines', 'salik', 'maintenance', 'loan_emi', 'prev_arrears',
  'paid_in_wallet', 'paid_as_tips', 'paid_as_cod',
  'misc_deduction', 'clawback_deduction', 'platform_deduction',
  'food_compensation', 'room_rent', 'slx_deduction',
  'fine_arrears', 'salary_advance', 'rider_pay_fines_total',
  // Stored originals (used for reverse-entry tracking)
  'original_fines', 'original_salik', 'original_maintenance',
  'original_loan_emi', 'original_room_rent',
];

export const round2 = (value) => {
  if (value === null || value === undefined || value === '') return value;
  const n = typeof value === 'number' ? value : parseFloat(value);
  if (!isFinite(n)) return value;
  // Use string trick to avoid double-rounding edge cases like 1.005.
  return Math.round((n + Number.EPSILON) * 100) / 100;
};

export const round2Row = (row) => {
  if (!row || typeof row !== 'object') return row;
  const out = { ...row };
  for (const k of MONEY_FIELDS) {
    if (out[k] !== undefined && out[k] !== null && out[k] !== '') {
      out[k] = round2(out[k]);
    }
  }
  return out;
};

export const round2PreviewRows = (rows) => {
  if (!Array.isArray(rows)) return rows;
  return rows.map(round2Row);
};

export default round2;
